import { TrackingEvent, BarcodeType } from '@caresend/types';
import { getUnknownErrorMessage } from '@caresend/utils';
import { getCurrentLocation } from '../getCurrentLocation.js';
import { addSentryBreadcrumb } from '../sentry.js';
import { analytics } from './index.js';
import { getRouter } from '../../router/helpers/initRouter.js';
import { getStore } from '../../store/helpers/initStore.js';

const addEventBreadcrumb = (event, data) => {
    addSentryBreadcrumb(event, 'Tracking Event', data);
};
const trackEvent = async (store, event, data, addGeo = false) => {
    var _a, _b, _c, _d;
    const router = getRouter();
    const { state } = store;
    const routeName = (_a = router.currentRoute.name) !== null && _a !== void 0 ? _a : '';
    let geo;
    let geoError;
    if (addGeo) {
        try {
            geo = await getCurrentLocation(store);
        }
        catch (error) {
            if (error instanceof GeolocationPositionError) {
                const codes = {
                    [error.PERMISSION_DENIED]: 'PERMISSION_DENIED',
                    [error.POSITION_UNAVAILABLE]: 'POSITION_UNAVAILABLE',
                    [error.TIMEOUT]: 'TIMEOUT',
                };
                geoError = codes[error.code];
            }
            else {
                geoError = getUnknownErrorMessage(error);
            }
        }
    }
    const { user } = state.auth;
    const userID = user ? user.id : '';
    const email = user ? user.info.email : '';
    const offices = user === null || user === void 0 ? void 0 : user.offices;
    const medicalTitle = (_c = (_b = user === null || user === void 0 ? void 0 : user.professionalInfo) === null || _b === void 0 ? void 0 : _b.title) !== null && _c !== void 0 ? _c : '';
    const timestamp = Date.now();
    (_d = analytics.track) === null || _d === void 0 ? void 0 : _d.call(analytics, event, {
        email,
        geo,
        geoError,
        medicalTitle,
        offices,
        route: routeName,
        timestamp,
        userID,
        ...data,
    });
    addEventBreadcrumb(event, data);
};
const trackDebugEvent = async (name, data) => {
    await trackEvent(getStore(), TrackingEvent.DEBUG, { name, ...data });
};
/** Tracked when the nurse hits "Drive" to any waypoint (patient visit, pickDrop) */
const trackDriveToWaypoint = async (waypointID) => {
    await trackEvent(getStore(), TrackingEvent.DRIVE_TO_WAYPOINT, {
        waypointID,
    }, true);
};
/**
 * Reports data on every scan. If a batchItem or supplyItem is scanned, its ID
 * is included.
 */
const trackScanItem = async (barcode, errorMessage) => {
    let supplyItemID;
    let batchItemID;
    let unknownBarcode;
    let barcodeValue;
    const barcodeType = barcode === null || barcode === void 0 ? void 0 : barcode.type;
    switch (barcode === null || barcode === void 0 ? void 0 : barcode.type) {
        case BarcodeType.BATCH_ITEM:
            batchItemID = barcode.id;
            break;
        case BarcodeType.SUPPLY_ITEM:
            supplyItemID = barcode.id;
            break;
        case BarcodeType.SHIPPING_LABEL:
            barcodeValue = barcode.id;
            break;
        default:
            unknownBarcode = barcode.id;
    }
    await trackEvent(getStore(), TrackingEvent.SCAN_ITEM, {
        barcodeType,
        barcodeValue,
        batchItemID,
        errorMessage,
        supplyItemID,
        unknownBarcode,
    }, true);
};
const trackFinishWaypoint = async (waypointID, additionalData) => {
    await trackEvent(getStore(), TrackingEvent.FINISH_WAYPOINT, {
        ...additionalData,
        waypointID,
    }, true);
};
// TEMPORARY FUNCTIONS TO UNDERSTAND PATIENT APP LOADING PERFORMANCE
const trackAppLaunch = () => {
    analytics.track(TrackingEvent.APP_LAUNCH, {
        timestamp: Date.now(),
        totalLoadTime: performance.now(),
        urlRoute: window.location.pathname,
    });
};
const trackInitializationEvent = (name) => {
    analytics.track(TrackingEvent.APP_INITIALIZATION_EVENT, {
        timestamp: Date.now(),
        totalLoadTime: performance.now(),
        urlRoute: window.location.pathname,
        name,
    });
};
const trackAppFinishLoading = () => {
    analytics.track(TrackingEvent.APP_FINISH_LOADING, {
        timestamp: Date.now(),
        totalLoadTime: performance.now(),
        urlRoute: window.location.pathname,
    });
};

export { addEventBreadcrumb, trackAppFinishLoading, trackAppLaunch, trackDebugEvent, trackDriveToWaypoint, trackEvent, trackFinishWaypoint, trackInitializationEvent, trackScanItem };
